import { useFlagsmithLoading } from 'flagsmith/react';
import { useEffect, useState } from 'react';

import { FEATURE_FLAGS } from '@/constants/featureFlags';
import { TEN_SECONDS } from '@/constants/time';
import { useFeatureFlag, useFeatureFlagValue } from '@/hooks/useFeatureFlag';

/** Which transport runs the chat. See the `chat_transport` flag for what each means. */
export type ChatTransportMode = 'twilio' | 'shadow' | 'primum';

const MODES: readonly ChatTransportMode[] = ['twilio', 'shadow', 'primum'];

/** Anything the flag might hold that is not a known mode reads as Twilio, the safe default. */
export function parseChatTransportMode(value: unknown): ChatTransportMode {
  return MODES.find((mode) => mode === value) ?? 'twilio';
}

/**
 * The mode the toggle selects. A disabled flag reads as Twilio whatever its
 * value, so switching the flag off in Flagsmith is the kill switch, as it is
 * for every other flag.
 */
export function useChatTransportMode(): ChatTransportMode {
  const isEnabled = useFeatureFlag(FEATURE_FLAGS.CHAT_TRANSPORT);

  const value = useFeatureFlagValue(FEATURE_FLAGS.CHAT_TRANSPORT);

  return isEnabled ? parseChatTransportMode(value) : 'twilio';
}

/** How long a session waits for flags before running on whatever they say. */
export const FLAGS_SETTLE_DEADLINE_MS = TEN_SECONDS;

/**
 * Whether the mode can be trusted yet.
 *
 * The splash is released on flag defaults when Flagsmith is slow, so a session
 * mounted straight away would run Twilio and then be torn down for the real
 * mode seconds later, building a client nobody uses. Waiting for Flagsmith
 * avoids that. The deadline keeps a Flagsmith outage from holding the chat
 * back for good: past it the session runs on the defaults, as everything else
 * already does.
 */
export function useChatTransportModeSettled(): boolean {
  const loading = useFlagsmithLoading();

  const haveFlagsSettled = loading != null && !loading.isLoading;

  const [hasDeadlinePassed, setHasDeadlinePassed] = useState(false);

  useEffect(() => {
    if (haveFlagsSettled) {
      return undefined;
    }

    const timer = setTimeout(() => setHasDeadlinePassed(true), FLAGS_SETTLE_DEADLINE_MS);

    return () => clearTimeout(timer);
  }, [haveFlagsSettled]);

  return haveFlagsSettled || hasDeadlinePassed;
}
